import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Select,
    SelectItem,
    Input,
    Spinner,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@heroui/react";
import {
    UploadSimple,
    FileText,
    X,
    CalendarBlank,
    TagSimple,
    Plus,
    FolderSimple,
    MagnifyingGlass,
    CaretDown,
    Link as LinkIcon,
} from "@phosphor-icons/react";
import {
    CompanyDocumentLabel,
    CompanyFolderStructure,
} from "shared/types/company-documents";
import { searchResourcesFromAPI } from "shared/api/fetch-resources";
import { Buildings, User } from "@phosphor-icons/react";

interface CompanyDocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete: () => void;
    companyId: number;
    labels: CompanyDocumentLabel[];
    onLabelsRefresh?: () => Promise<void> | void;
}

interface FlatFolderOption {
    id: number;
    label: string;
}

function flattenFolders(
    folders: CompanyFolderStructure[],
    depth = 0,
): FlatFolderOption[] {
    const result: FlatFolderOption[] = [];
    for (const folder of folders) {
        const indent = depth > 0 ? `${"  ".repeat(depth - 1)}└ ` : "";
        result.push({ id: folder.id, label: `${indent}${folder.name}` });
        if (folder.children && folder.children.length > 0) {
            result.push(...flattenFolders(folder.children, depth + 1));
        }
    }
    return result;
}

const CompanyDocumentUploadModal = ({
    isOpen,
    onClose,
    onUploadComplete,
    companyId,
    labels,
    onLabelsRefresh,
}: CompanyDocumentUploadModalProps) => {
    const { session } = useSession();
    const [file, setFile] = useState<File | null>(null);
    const [selectedLabelId, setSelectedLabelId] = useState<string>("");
    const [summary, setSummary] = useState("");
    const [documentDate, setDocumentDate] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Folder structure for grouping + inline label creation
    const [folders, setFolders] = useState<CompanyFolderStructure[]>([]);

    // Inline label creation state
    const [isCreatingLabelOpen, setIsCreatingLabelOpen] = useState(false);
    const [newLabelName, setNewLabelName] = useState("");
    const [newLabelFolderId, setNewLabelFolderId] = useState<string>("");
    const [isSavingLabel, setIsSavingLabel] = useState(false);

    // Relations
    const [relations, setRelations] = useState<
        { id: number; name: string; type: string }[]
    >([]);
    const [relationSearch, setRelationSearch] = useState("");
    const [relationResults, setRelationResults] = useState<
        { id: number; name: string; type: string; metadata1?: string }[]
    >([]);
    const [isSearchingRelations, setIsSearchingRelations] = useState(false);

    // Label picker state
    const [isLabelPickerOpen, setIsLabelPickerOpen] = useState(false);
    const [labelSearch, setLabelSearch] = useState("");
    const [rootFolderFilterId, setRootFolderFilterId] = useState<number | null>(
        null,
    );

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

    useEffect(() => {
        if (!isOpen) return;
        fetchFolders();
    }, [isOpen]);

    // Debounced relation search — sorts startsWith > contains and dedupes already-added
    useEffect(() => {
        const term = relationSearch.trim();
        if (term.length < 2) {
            setRelationResults([]);
            setIsSearchingRelations(false);
            return;
        }
        let cancelled = false;
        setIsSearchingRelations(true);
        const timer = setTimeout(async () => {
            try {
                const data = await searchResourcesFromAPI(
                    session,
                    term,
                    "contact,company",
                );
                if (cancelled) return;
                const lower = term.toLowerCase();
                const sorted = [...(data || [])].sort((a: any, b: any) => {
                    const an = (a.name || "").toLowerCase();
                    const bn = (b.name || "").toLowerCase();
                    const aStarts = an.startsWith(lower) ? 0 : 1;
                    const bStarts = bn.startsWith(lower) ? 0 : 1;
                    if (aStarts !== bStarts) return aStarts - bStarts;
                    return an.localeCompare(bn);
                });
                setRelationResults(sorted);
            } catch (err) {
                if (cancelled) return;
                Sentry.captureException(err);
                console.error("Error searching relations:", err);
                setRelationResults([]);
            } finally {
                if (!cancelled) setIsSearchingRelations(false);
            }
        }, 200);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [relationSearch, session]);

    const fetchFolders = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/folder-structure`,
                { method: "GET" },
                session,
            );
            if (!response.ok)
                throw new Error(`Failed to fetch folder structure: ${response.status}`);
            const data = await response.json();
            setFolders(data);
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching company folder structure:", error);
            toast.error("Fout bij ophalen van mappenstructuur");
        }
    };

    const resetForm = () => {
        setFile(null);
        setSelectedLabelId("");
        setSummary("");
        setDocumentDate("");
        setIsCreatingLabelOpen(false);
        setNewLabelName("");
        setNewLabelFolderId("");
        setRelations([]);
        setRelationSearch("");
        setRelationResults([]);
        setLabelSearch("");
        setRootFolderFilterId(null);
        setIsLabelPickerOpen(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleFileSelect = (selectedFile: File) => {
        if (selectedFile.size > MAX_FILE_SIZE) {
            const sizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
            toast.error(`Bestand is te groot (${sizeMB}MB). Maximum is 20MB.`);
            return;
        }
        setFile(selectedFile);
    };

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileSelect(droppedFile);
        }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (selected) {
            handleFileSelect(selected);
        }
    };

    const handleCreateLabel = async () => {
        const trimmedName = newLabelName.trim();
        if (!trimmedName) {
            toast.error("Geef het label een naam");
            return;
        }
        if (!newLabelFolderId) {
            toast.error("Selecteer een map voor het label");
            return;
        }

        setIsSavingLabel(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/company-documents/labels`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        label: trimmedName,
                        folderStructureId: parseInt(newLabelFolderId),
                    }),
                },
                session,
            );

            if (!response.ok) {
                let message = "Fout bij aanmaken van label";
                try {
                    const err = await response.json();
                    if (err?.message)
                        message = Array.isArray(err.message)
                            ? err.message.join(", ")
                            : err.message;
                } catch {
                    /* ignore parse errors */
                }
                throw new Error(message);
            }

            const created: CompanyDocumentLabel = await response.json();
            await onLabelsRefresh?.();
            setSelectedLabelId(created.id.toString());
            setIsCreatingLabelOpen(false);
            setNewLabelName("");
            setNewLabelFolderId("");
            toast.success("Label aangemaakt en geselecteerd");
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error creating company document label:", error);
            toast.error(
                error instanceof Error ? error.message : "Fout bij aanmaken van label",
            );
        } finally {
            setIsSavingLabel(false);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Selecteer een bestand");
            return;
        }
        if (!selectedLabelId) {
            toast.error("Selecteer een label");
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const documentData: Record<string, unknown> = {
                labelId: parseInt(selectedLabelId),
            };
            if (summary.trim()) documentData.summary = summary.trim();
            if (documentDate) documentData.documentDate = documentDate;
            if (relations.length > 0) {
                documentData.relations = relations.map((r) => ({
                    id: r.id,
                    resourceId: String(r.id),
                    type: r.type,
                    name: r.name,
                }));
            }

            formData.append("documentData", JSON.stringify(documentData));

            const response = await fetchWithAuth(
                `${API_URL}/company-documents/companies/${companyId}/upload`,
                {
                    method: "POST",
                    body: formData,
                },
                session,
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload mislukt: ${response.status} — ${errorText}`);
            }

            toast.success("Document succesvol geüpload");
            resetForm();
            onUploadComplete();
            onClose();
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    context: "CompanyDocumentUpload",
                    companyId,
                    labelId: selectedLabelId,
                },
            });
            console.error("Error uploading company document:", error);
            toast.error("Fout bij uploaden van document");
        } finally {
            setIsUploading(false);
        }
    };

    const flatFolders = useMemo(() => flattenFolders(folders), [folders]);

    const selectedLabel = labels.find((l) => l.id.toString() === selectedLabelId);

    // Map every folder id (root or nested) to its root folder
    const folderIdToRoot = useMemo(() => {
        const map = new Map<number, { id: number; name: string }>();
        const walk = (
            node: CompanyFolderStructure,
            root: { id: number; name: string },
        ) => {
            map.set(node.id, root);
            (node.children || []).forEach((child) => walk(child, root));
        };
        for (const root of folders) {
            walk(root, { id: root.id, name: root.name });
        }
        return map;
    }, [folders]);

    // Labels filtered by search + root-folder filter, then grouped by their direct folder
    const filteredGroupedLabels = useMemo(() => {
        const search = labelSearch.trim().toLowerCase();

        const matches = labels.filter((label) => {
            if (rootFolderFilterId !== null) {
                const root = label.folderStructure
                    ? folderIdToRoot.get(label.folderStructure.id)
                    : undefined;
                if (!root || root.id !== rootFolderFilterId) return false;
            }
            if (!search) return true;
            const haystack = `${label.label} ${label.description || ""}`.toLowerCase();
            return haystack.includes(search);
        });

        const groups = new Map<
            string,
            {
                folderName: string;
                folderId: number | null;
                rootName: string | null;
                labels: CompanyDocumentLabel[];
            }
        >();
        const orphanKey = "__orphan__";

        for (const label of matches) {
            const folderId = label.folderStructure?.id ?? null;
            const folderName = label.folderStructure?.name ?? "Geen map";
            const root = label.folderStructure
                ? folderIdToRoot.get(label.folderStructure.id)
                : undefined;
            const rootName = root?.name ?? null;
            const key = folderId !== null ? folderId.toString() : orphanKey;
            if (!groups.has(key)) {
                groups.set(key, { folderName, folderId, rootName, labels: [] });
            }
            groups.get(key)!.labels.push(label);
        }

        const sorted = Array.from(groups.values()).sort((a, b) =>
            a.folderName.localeCompare(b.folderName),
        );
        for (const group of sorted) {
            group.labels.sort((a, b) => a.label.localeCompare(b.label));
        }
        return sorted;
    }, [labels, labelSearch, rootFolderFilterId, folderIdToRoot]);

    const totalFilteredLabels = filteredGroupedLabels.reduce(
        (acc, g) => acc + g.labels.length,
        0,
    );

    return (
        <Modal size="2xl" isOpen={isOpen} onClose={handleClose}>
            <ModalContent>
                <ModalHeader className="flex items-center gap-2 border-b border-gray-200">
                    <UploadSimple
                        className="text-moss-primary h-5 w-5"
                        weight="duotone"
                    />
                    <span>Document uploaden</span>
                </ModalHeader>

                <ModalBody className="flex flex-col gap-5 py-5">
                    {/* File drop zone */}
                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                            isDragging
                                ? "border-moss-primary bg-moss-primary/5"
                                : file
                                  ? "border-moss-primary/40 bg-moss-primary/5"
                                  : "border-gray-300 hover:border-gray-400"
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleInputChange}
                        />
                        {file ? (
                            <div className="flex items-center gap-3">
                                <FileText
                                    className="text-moss-primary h-8 w-8"
                                    weight="duotone"
                                />
                                <div className="flex flex-col">
                                    <span className="text-neutral-primary font-medium">
                                        {file.name}
                                    </span>
                                    <span className="text-neutral-secondary-mid text-sm">
                                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                                    </span>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFile(null);
                                    }}
                                    className="text-neutral-secondary-mid ml-2 rounded p-1 transition-colors hover:bg-red-50 hover:text-red-600"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <UploadSimple className="text-neutral-secondary-mid h-8 w-8" />
                                <p className="text-neutral-secondary-mid text-sm">
                                    Klik of sleep een bestand hierheen
                                </p>
                                <p className="text-xs text-gray-400">Maximaal 20MB</p>
                            </div>
                        )}
                    </div>

                    {/* Label selector with hierarchy + inline create */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                                <TagSimple
                                    className="text-neutral-secondary-mid h-4 w-4"
                                    weight="duotone"
                                />
                                <span className="text-neutral-primary text-sm font-medium">
                                    Documentlabel
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsCreatingLabelOpen((prev) => !prev)}
                                className="text-moss-primary hover:bg-moss-primary/5 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors"
                            >
                                <Plus size={12} weight="bold" />
                                {isCreatingLabelOpen ? "Sluiten" : "Nieuw label"}
                            </button>
                        </div>

                        {labels.length > 0 ? (
                            <Popover
                                isOpen={isLabelPickerOpen}
                                onOpenChange={setIsLabelPickerOpen}
                                placement="bottom-start"
                            >
                                <PopoverTrigger>
                                    <button
                                        type="button"
                                        className="hover:border-moss-primary/40 flex w-full items-center justify-between rounded-lg border-2 border-neutral-200 bg-white px-3 py-2 text-left transition-colors"
                                    >
                                        {selectedLabel ? (
                                            <span className="text-neutral-primary text-sm">
                                                {selectedLabel.label}
                                                {selectedLabel.folderStructure && (
                                                    <span className="text-neutral-secondary-mid ml-1.5">
                                                        ·{" "}
                                                        {selectedLabel.folderStructure.name}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="text-neutral-secondary-mid text-sm">
                                                Selecteer een label
                                            </span>
                                        )}
                                        <CaretDown
                                            size={14}
                                            className="text-neutral-secondary-mid"
                                        />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[460px] max-w-[90vw] p-0"
                                >
                                    <div className="flex w-full flex-col">
                                        <div className="flex flex-col gap-2 border-b border-neutral-200 p-3">
                                            <div className="relative">
                                                <MagnifyingGlass
                                                    size={14}
                                                    className="text-neutral-secondary-mid pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
                                                />
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    value={labelSearch}
                                                    onChange={(e) =>
                                                        setLabelSearch(e.target.value)
                                                    }
                                                    placeholder="Zoek label..."
                                                    className="text-neutral-primary placeholder:text-neutral-secondary-mid focus:border-moss-primary w-full rounded-md border border-neutral-200 bg-white py-1.5 pl-7 pr-2 text-sm focus:outline-none"
                                                />
                                            </div>
                                            {folders.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setRootFolderFilterId(null)
                                                        }
                                                        className={`rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${
                                                            rootFolderFilterId === null
                                                                ? "border-moss-primary bg-moss-primary text-white"
                                                                : "border-neutral-200 bg-white text-neutral-secondary-mid hover:border-moss-primary/40"
                                                        }`}
                                                    >
                                                        Alle
                                                    </button>
                                                    {folders.map((root) => (
                                                        <button
                                                            key={root.id}
                                                            type="button"
                                                            onClick={() =>
                                                                setRootFolderFilterId(
                                                                    rootFolderFilterId ===
                                                                        root.id
                                                                        ? null
                                                                        : root.id,
                                                                )
                                                            }
                                                            className={`rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${
                                                                rootFolderFilterId ===
                                                                root.id
                                                                    ? "border-moss-primary bg-moss-primary text-white"
                                                                    : "border-neutral-200 bg-white text-neutral-secondary-mid hover:border-moss-primary/40"
                                                            }`}
                                                        >
                                                            {root.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="max-h-[280px] overflow-y-auto p-2">
                                            {totalFilteredLabels === 0 ? (
                                                <p className="text-neutral-secondary-mid px-2 py-4 text-center text-sm">
                                                    Geen labels gevonden
                                                </p>
                                            ) : (
                                                filteredGroupedLabels.map((group) => (
                                                    <div
                                                        key={
                                                            group.folderId !== null
                                                                ? `folder-${group.folderId}`
                                                                : "orphan"
                                                        }
                                                        className="mb-2 last:mb-0"
                                                    >
                                                        <div className="text-neutral-secondary-mid flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide">
                                                            <FolderSimple
                                                                size={11}
                                                                weight="duotone"
                                                                className="text-moss-primary"
                                                            />
                                                            <span>
                                                                {group.rootName &&
                                                                group.rootName !==
                                                                    group.folderName
                                                                    ? `${group.rootName} › ${group.folderName}`
                                                                    : group.folderName}
                                                            </span>
                                                        </div>
                                                        {group.labels.map((label) => {
                                                            const isSelected =
                                                                selectedLabelId ===
                                                                label.id.toString();
                                                            return (
                                                                <button
                                                                    key={label.id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedLabelId(
                                                                            label.id.toString(),
                                                                        );
                                                                        setIsLabelPickerOpen(
                                                                            false,
                                                                        );
                                                                    }}
                                                                    className={`flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left transition-colors ${
                                                                        isSelected
                                                                            ? "bg-moss-primary/10 text-moss-primary"
                                                                            : "hover:bg-neutral-100 text-neutral-primary"
                                                                    }`}
                                                                >
                                                                    <span className="text-sm font-medium">
                                                                        {label.label}
                                                                    </span>
                                                                    {label.description && (
                                                                        <span className="text-neutral-secondary-mid text-xs">
                                                                            {label.description}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <p className="text-neutral-secondary-mid text-sm">
                                Geen labels beschikbaar. Maak er hieronder een aan.
                            </p>
                        )}

                        {isCreatingLabelOpen && (
                            <div className="border-moss-primary/30 bg-moss-primary/5 mt-1 flex flex-col gap-2 rounded-lg border p-3">
                                <p className="text-neutral-primary text-sm font-semibold">
                                    Nieuw label aanmaken
                                </p>
                                <Select
                                    label="In map"
                                    size="sm"
                                    placeholder="Selecteer een map"
                                    selectedKeys={
                                        newLabelFolderId ? [newLabelFolderId] : []
                                    }
                                    onChange={(e) =>
                                        setNewLabelFolderId(e.target.value)
                                    }
                                    variant="bordered"
                                    startContent={
                                        <FolderSimple
                                            size={14}
                                            weight="duotone"
                                            className="text-moss-primary"
                                        />
                                    }
                                    classNames={{
                                        label: "text-xs",
                                        value: "text-sm",
                                    }}
                                >
                                    {flatFolders.map((f) => (
                                        <SelectItem
                                            key={f.id.toString()}
                                            textValue={f.label.trim()}
                                        >
                                            <span className="font-mono text-sm">
                                                {f.label}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </Select>
                                <Input
                                    label="Labelnaam"
                                    size="sm"
                                    placeholder="bv. Notarisakte"
                                    value={newLabelName}
                                    onChange={(e) => setNewLabelName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleCreateLabel();
                                    }}
                                    variant="bordered"
                                    classNames={{
                                        label: "text-xs",
                                        input: "text-sm",
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button
                                        size="sm"
                                        variant="light"
                                        onPress={() => {
                                            setIsCreatingLabelOpen(false);
                                            setNewLabelName("");
                                            setNewLabelFolderId("");
                                        }}
                                        isDisabled={isSavingLabel}
                                    >
                                        Annuleren
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="bg-moss-primary text-white"
                                        onPress={handleCreateLabel}
                                        isDisabled={isSavingLabel}
                                        startContent={
                                            isSavingLabel ? (
                                                <Spinner size="sm" color="white" />
                                            ) : (
                                                <Plus size={14} weight="bold" />
                                            )
                                        }
                                    >
                                        Aanmaken
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    <Input
                        label="Samenvatting (optioneel)"
                        placeholder="Korte beschrijving van het document"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        variant="bordered"
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                    />

                    {/* Relations */}
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1.5">
                            <LinkIcon
                                className="text-neutral-secondary-mid h-4 w-4"
                                weight="duotone"
                            />
                            <span className="text-neutral-primary text-sm font-medium">
                                Relaties (optioneel)
                            </span>
                        </div>
                        {relations.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {relations.map((r) => (
                                    <span
                                        key={`${r.type}-${r.id}`}
                                        className="text-neutral-primary inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white pl-2 pr-1 text-xs font-medium"
                                    >
                                        {r.name}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setRelations((prev) =>
                                                    prev.filter(
                                                        (x) =>
                                                            !(
                                                                x.id === r.id &&
                                                                x.type === r.type
                                                            ),
                                                    ),
                                                )
                                            }
                                            className="text-neutral-secondary-mid ml-0.5 rounded-full p-0.5 transition-colors hover:bg-red-50 hover:text-red-600"
                                            aria-label={`Relatie ${r.name} verwijderen`}
                                        >
                                            <X size={12} weight="bold" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-col gap-1.5">
                            <div className="relative">
                                <MagnifyingGlass
                                    size={14}
                                    className="text-neutral-secondary-mid pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
                                />
                                <input
                                    type="text"
                                    value={relationSearch}
                                    onChange={(e) => setRelationSearch(e.target.value)}
                                    placeholder="Zoek op contact of vennootschap..."
                                    className="text-neutral-primary placeholder:text-neutral-secondary-mid focus:border-moss-primary w-full rounded-md border border-neutral-200 bg-white py-2 pl-8 pr-3 text-sm focus:outline-none"
                                />
                                {isSearchingRelations && (
                                    <Spinner
                                        size="sm"
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2"
                                    />
                                )}
                            </div>
                            {relationSearch.trim().length >= 2 && (
                                <div className="max-h-[260px] overflow-y-auto rounded-md border border-neutral-200 bg-white">
                                    {relationResults.length === 0 &&
                                    !isSearchingRelations ? (
                                        <p className="text-neutral-secondary-mid px-3 py-3 text-center text-sm">
                                            Geen resultaten
                                        </p>
                                    ) : (
                                        relationResults.map((r) => {
                                            const alreadyAdded = relations.some(
                                                (x) =>
                                                    x.id === r.id && x.type === r.type,
                                            );
                                            const isCompany = r.type === "company";
                                            return (
                                                <button
                                                    key={`${r.type}-${r.id}`}
                                                    type="button"
                                                    disabled={alreadyAdded}
                                                    onClick={() => {
                                                        setRelations((prev) =>
                                                            prev.some(
                                                                (x) =>
                                                                    x.id === r.id &&
                                                                    x.type === r.type,
                                                            )
                                                                ? prev
                                                                : [
                                                                      ...prev,
                                                                      {
                                                                          id: r.id,
                                                                          name: r.name,
                                                                          type: r.type,
                                                                      },
                                                                  ],
                                                        );
                                                        setRelationSearch("");
                                                        setRelationResults([]);
                                                    }}
                                                    className={`flex w-full items-center gap-2 border-b border-neutral-100 px-3 py-2 text-left last:border-b-0 ${
                                                        alreadyAdded
                                                            ? "cursor-not-allowed opacity-50"
                                                            : "hover:bg-neutral-50"
                                                    }`}
                                                >
                                                    {isCompany ? (
                                                        <Buildings
                                                            size={14}
                                                            weight="duotone"
                                                            className="text-moss-primary shrink-0"
                                                        />
                                                    ) : (
                                                        <User
                                                            size={14}
                                                            weight="duotone"
                                                            className="text-moss-primary shrink-0"
                                                        />
                                                    )}
                                                    <div className="flex min-w-0 flex-1 flex-col">
                                                        <span className="text-neutral-primary truncate text-sm font-medium">
                                                            {r.name}
                                                        </span>
                                                        {r.metadata1 && (
                                                            <span className="text-neutral-secondary-mid truncate text-xs">
                                                                {r.metadata1}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-neutral-secondary-mid shrink-0 text-[10px] uppercase tracking-wide">
                                                        {isCompany ? "Vennootschap" : "Contact"}
                                                    </span>
                                                    {alreadyAdded && (
                                                        <span className="text-neutral-secondary-mid shrink-0 text-[10px]">
                                                            toegevoegd
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                            {relationSearch.trim().length > 0 &&
                                relationSearch.trim().length < 2 && (
                                    <p className="text-neutral-secondary-mid text-xs italic">
                                        Typ minstens 2 letters om te zoeken
                                    </p>
                                )}
                        </div>
                    </div>

                    {/* Document date */}
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1.5">
                            <CalendarBlank
                                className="text-neutral-secondary-mid h-4 w-4"
                                weight="duotone"
                            />
                            <span className="text-neutral-primary text-sm font-medium">
                                Documentdatum (optioneel)
                            </span>
                        </div>
                        <input
                            type="date"
                            value={documentDate}
                            onChange={(e) => setDocumentDate(e.target.value)}
                            className="text-neutral-primary focus:border-moss-primary rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none"
                        />
                    </div>
                </ModalBody>

                <ModalFooter className="border-t border-gray-200">
                    <Button variant="light" onPress={handleClose}>
                        Annuleren
                    </Button>
                    <Button
                        className="bg-moss-primary text-white"
                        onPress={handleUpload}
                        isDisabled={isUploading || !file || !selectedLabelId}
                        startContent={
                            isUploading ? (
                                <Spinner size="sm" color="white" />
                            ) : (
                                <UploadSimple size={16} />
                            )
                        }
                    >
                        {isUploading ? "Bezig met uploaden..." : "Uploaden"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CompanyDocumentUploadModal;
